/**
 * The AI assist layer: feature vocabulary, per-company settings, and the
 * sanitizer everything reads stored settings through.
 *
 * Shared between the Worker (which decides whether an assist may run and
 * meters what it costs) and the web app (which edits the toggles and shows
 * the month's usage), so both sides agree on what every switch means.
 *
 * Two principles hold everywhere this file is consumed:
 *
 *  1. AI proposes, the engine decides. Nothing a model returns is ever a
 *     price, a schedule change, or a sent message by itself — it lands as a
 *     structured proposal that deterministic code prices and a person
 *     confirms. Model output is untrusted input, exactly like a request body.
 *  2. Every plan gets the everyday assists; the plans differ in how much.
 *     The monthly credit allowance below is the per-plan difference, and the
 *     only assist behind a PlanFeature gate is Magic Quote — everything else
 *     runs on Starter, because "AI on Starter" was a product decision.
 */

import { z } from 'zod';
import { ROOM_CONDITION_CODES, ROOM_SIZE_CODES, type PlanId } from './constants/index.ts';
import { DEFAULT_MAGIC_QUOTE_SETTINGS, sanitizeMagicQuoteSettings, type MagicQuoteSettings } from './magic-quote.ts';

/**
 * Everything that costs one action from the company's monthly allowance.
 * One vocabulary for metering, audit rows and the usage endpoint, so a row
 * written by one feature can never be misread by another.
 */
export const AI_ACTIONS = [
  // A lead's free text (and photos) parsed into a draft-quote proposal.
  'lead_draft',
  // A vision pass over a lead's photos: condition, clutter, caveats.
  'photo_assessment',
  // An inbound customer text classified into an actionable intent.
  'sms_intent',
  // An inbound customer EMAIL classified the same way. Its own action rather
  // than a reuse: an email is longer, costs more to read, and a company may
  // reasonably want one and not the other.
  'email_intent',
  // A suggested reply to what a customer just said, for a person to edit.
  'reply_draft',
  // A voicemail turned into text by Whisper on Workers AI. Governed by the
  // master switch and the company's own "Transcribe voicemails" setting.
  'voicemail_transcription',
  // An estimated-vs-actual analysis turned into task-minute suggestions.
  'tuning_suggestions',
  // The owner's weekly plain-language digest.
  'weekly_digest',
  // A personalized quote follow-up message.
  'follow_up_copy',
  // The crew-briefing paragraph on a visit's printable checklist.
  'crew_briefing',
  // A post-visit review request message.
  'review_request',
  // A drafted reply to a customer review the office pasted in.
  'review_reply',
  // A Quick Quote's initial property parse. The final canonical quote build
  // is deterministic and does not spend another action.
  'quick_quote_parse',
  // Only an ambiguous free-text follow-up that needs reinterpretation. Opaque
  // option answers, number answers and Room Check confirmation cost nothing.
  'quick_quote_clarify',
  // One photo a team member adds to a Quick Quote, read once as it is
  // uploaded into tags that join the typed description in the parse.
  'quick_quote_photo',
  // One walkthrough capture read by the pipeline: a photo assessed, a
  // recording transcribed, a note parsed. Metered per capture because that is
  // what actually costs — a 40-photo walkthrough is not one photo's work.
  'magic_quote_capture',
  // A walkthrough synthesized and turned into a draft quote. One action for
  // the whole reconcile-and-generate pass, however many rooms it produced.
  'magic_quote_generate',
  // One conversational edit to a generated draft ("make the first clean
  // $325"). Cheap by construction: it reads the structured context, not media.
  'magic_quote_chat',
  // Photo quotes: one batch of up to three customer photos read into room,
  // condition and privacy tags. Metered per batch, so thirty photos cost ten.
  'photo_dump_triage',
  // Photo quotes: the batch tags (text, not images) reconciled into rooms
  // from the company's own library, counts, condition and size.
  'photo_dump_synthesis',
] as const;
export type AiAction = (typeof AI_ACTIONS)[number];

export function isAiAction(value: unknown): value is AiAction {
  return typeof value === 'string' && (AI_ACTIONS as readonly string[]).includes(value);
}

/**
 * Customer-facing action weights. These are fairness credits, not a dollar
 * conversion or a provider-cost bound. Failed/unusable work can refund these
 * credits while a billed or uncertain provider attempt remains in the separate
 * provider-spend ledger. When cost guards are active, both allowances apply
 * and the Settings usage response exposes the provider allowance as well.
 */
export const AI_ACTION_CREDITS: Readonly<Record<AiAction, number>> = {
  // Haiku, a few hundred tokens each way. Rounds to nothing; charged one
  // credit because zero-cost actions are a metering hole, not a kindness.
  sms_intent: 1,
  // Haiku again, but an email body is longer than a text — still well under a
  // credit, and charged one for the same reason `sms_intent` is.
  email_intent: 1,
  // Short prose off a small structured prompt: the last few messages and the
  // customer's upcoming visits. Priced with the other drafting assists.
  reply_draft: 1,
  // Up to three minutes of phone audio through Whisper turbo: a fraction of a
  // cent, charged one credit like the other per-message assists.
  voicemail_transcription: 1,
  // Short copy off a small structured prompt.
  follow_up_copy: 1,
  crew_briefing: 1,
  review_request: 1,
  review_reply: 1,
  // A short reinterpretation of one ambiguous free-text correction.
  quick_quote_clarify: 2,
  // One bounded structured parse of a property paragraph and room library.
  quick_quote_parse: 4,
  // One photo read into tags, the same image cost as a walkthrough photo.
  quick_quote_photo: 1,
  // One photo, clip or note read. A photo is the dear one (a 1920px image is
  // ~3.7k input tokens on its own); notes and sampled frames cost less.
  magic_quote_capture: 1,
  // Reads the structured draft, not the media, and answers briefly.
  magic_quote_chat: 2,
  // A month of estimated-vs-actual history in, a page of suggestions out.
  weekly_digest: 3,
  tuning_suggestions: 4,
  // Both may carry several photos, and both are allowed a long answer.
  lead_draft: 4,
  photo_assessment: 4,
  // The expensive one, by an order of magnitude: every capture's findings and
  // the whole room library in, up to 16k tokens of rooms and observations
  // out. Also what price advice costs, which shares this action.
  magic_quote_generate: 12,
  // Three photos at ~3.7k tokens each plus a short answer per photo: about a
  // credit a photo, the same rate as a walkthrough capture.
  photo_dump_triage: 3,
  // Tags and the room library in, a few thousand tokens of rooms out. Priced
  // with the other structured text passes (lead_draft, quick_quote_parse).
  photo_dump_synthesis: 4,
};

/** What one use of an assist costs. */
export function aiActionCredits(action: AiAction): number {
  return AI_ACTION_CREDITS[action];
}

/** Company-pooled monthly fairness credits. No additional credits per location.
 * These action weights do not guarantee a number of completed provider calls;
 * the separately disclosed provider allowance and abuse limits also apply.
 */
export const AI_MONTHLY_CREDITS: Readonly<Record<PlanId, number>> = {
  // Free is the plan that costs the deployment nothing to run, and a model
  // bill is the one cost a free company could still generate. No allowance,
  // so every assist refuses the same way it does for a company that has spent
  // its month — a quota answer, not a plan gate, because the way out is the
  // same either way: the assists come with a paid plan.
  free: 0,
  starter: 250,
  pro: 700,
  business: 1500,
};

/**
 * The most a single company may spend in one day, as a share of its month.
 *
 * The monthly allowance already bounds what a tenant can cost; this bounds
 * how fast. It exists for two failure modes the monthly number cannot see: a
 * client bug that retries in a loop burns the month before anyone notices,
 * and a tenant sprinting through its allowance in an hour can push the
 * deployment into its own daily ceiling and take everybody else's assists
 * down with it. A third of the month in a day is well past any honest day's
 * work — Pro's share is more than two full walkthroughs — while still
 * turning a runaway into a bounded incident.
 */
export const AI_DAILY_BURST_SHARE = 3;

/** The daily ceiling for a company on a given monthly allowance. */
export function aiDailyCreditCap(monthlyAllowance: number): number {
  // Floored so the smallest plan still gets a usable day: a third of 250 is
  // 84, which a single busy morning of lead drafts would hit.
  return Math.max(100, Math.ceil(monthlyAllowance / AI_DAILY_BURST_SHARE));
}

/**
 * Deployment-wide daily ceiling across every tenant, in credits — the
 * operator's own abuse cap, not anybody's budget. Mirrors the SMS quota
 * philosophy: the model API key belongs to whoever runs the deployment, so
 * one runaway tenant (or one bug) must never be able to spend everyone's
 * money.
 *
 * 5,000 credits is on the order of $150 of model spend a day, and covers
 * roughly a hundred Business companies all running at their full monthly
 * allowance spread across the month. An operator serving more should raise
 * it deliberately, having looked at the bill.
 */
export const AI_DEPLOYMENT_DAILY_CREDITS = 5_000;

export interface AiSettings {
  /**
   * The master switch. Off = no assist runs and nothing is metered, whatever
   * the individual toggles say. On by default: the deployment only has the
   * feature at all once its operator supplies a model API key.
   */
  enabled: boolean;
  /** Parse lead descriptions into pre-filled draft-quote proposals. */
  leadDrafts: boolean;
  /** Assess lead photos for condition and clutter alongside the draft. */
  photoAssessments: boolean;
  /** Triage inbound customer texts into confirm/move/cancel/question. */
  smsIntents: boolean;
  /** Suggest task-minute adjustments from estimated-vs-actual history. */
  tuningSuggestions: boolean;
  /**
   * The owner's Monday-morning digest email. Off by default: it SENDS
   * something, and nobody should get new recurring mail because a deploy
   * happened.
   */
  weeklyDigest: boolean;
  /**
   * Personalize the automated quote follow-up wording. Off by default for
   * the same reason: the words reach a customer without a person reviewing
   * them first.
   */
  followUpCopy: boolean;
  /** Write the crew-briefing paragraph on printable visit checklists. */
  crewBriefings: boolean;
  /**
   * Send a review request after a completed, paid visit. Off by default —
   * customer-facing, and review solicitation is a choice some companies
   * deliberately avoid.
   */
  reviewRequests: boolean;
  /** Quick Quote, from a typed description, photos or both. Independent of the Pro-only Magic walkthrough. */
  quickQuotes: boolean;
  /**
   * Magic Quote — the walkthrough capture system, and the only assist with
   * settings of its own rather than a single toggle: which capture methods a
   * company allows is a policy question (a company may want photos but not
   * audio in customers' homes), so it needs more than one bit.
   */
  magicQuote: MagicQuoteSettings;
}

export const DEFAULT_AI_SETTINGS: AiSettings = {
  enabled: true,
  leadDrafts: true,
  photoAssessments: true,
  smsIntents: true,
  tuningSuggestions: true,
  weeklyDigest: false,
  followUpCopy: false,
  crewBriefings: true,
  reviewRequests: false,
  quickQuotes: true,
  magicQuote: DEFAULT_MAGIC_QUOTE_SETTINGS,
};

function flag(raw: unknown, fallback: boolean): boolean {
  return typeof raw === 'boolean' ? raw : fallback;
}

/**
 * Whatever shape arrives, a complete `AiSettings` comes back. Field by field:
 * booleans pass, everything else lands on the default — a bad value in the
 * stored JSON costs that one toggle its customization, never the whole set.
 */
export function sanitizeAiSettings(raw: unknown): AiSettings {
  const source = raw !== null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    enabled: flag(source.enabled, DEFAULT_AI_SETTINGS.enabled),
    leadDrafts: flag(source.leadDrafts, DEFAULT_AI_SETTINGS.leadDrafts),
    photoAssessments: flag(source.photoAssessments, DEFAULT_AI_SETTINGS.photoAssessments),
    smsIntents: flag(source.smsIntents, DEFAULT_AI_SETTINGS.smsIntents),
    tuningSuggestions: flag(source.tuningSuggestions, DEFAULT_AI_SETTINGS.tuningSuggestions),
    weeklyDigest: flag(source.weeklyDigest, DEFAULT_AI_SETTINGS.weeklyDigest),
    followUpCopy: flag(source.followUpCopy, DEFAULT_AI_SETTINGS.followUpCopy),
    crewBriefings: flag(source.crewBriefings, DEFAULT_AI_SETTINGS.crewBriefings),
    reviewRequests: flag(source.reviewRequests, DEFAULT_AI_SETTINGS.reviewRequests),
    quickQuotes: flag(source.quickQuotes, DEFAULT_AI_SETTINGS.quickQuotes),
    // The one nested value: its own sanitizer, same field-by-field contract.
    magicQuote: sanitizeMagicQuoteSettings(source.magicQuote),
  };
}

/** The stored column (JSON text or NULL) → settings. Bad JSON = defaults. */
export function parseAiSettings(stored: string | null | undefined): AiSettings {
  if (!stored) return { ...DEFAULT_AI_SETTINGS };
  try {
    return sanitizeAiSettings(JSON.parse(stored));
  } catch {
    return { ...DEFAULT_AI_SETTINGS };
  }
}

/**
 * Magic Quote walkthrough actions are governed by their nested capture policy.
 * Quick Quote deliberately is not in this list: it is a Starter+ text assist
 * with its own top-level switch.
 */
const MAGIC_QUOTE_ACTIONS: readonly AiAction[] = ['magic_quote_capture', 'magic_quote_generate', 'magic_quote_chat'];

/** The settings fields that are a plain on/off switch over one action. */
type BooleanSettingKey = Exclude<keyof AiSettings, 'magicQuote'>;

/** Which per-feature toggle governs each metered action. */
export const AI_ACTION_TOGGLES: Readonly<
  Record<Exclude<AiAction, 'magic_quote_capture' | 'magic_quote_generate' | 'magic_quote_chat'>, BooleanSettingKey>
> = {
  lead_draft: 'leadDrafts',
  photo_assessment: 'photoAssessments',
  sms_intent: 'smsIntents',
  // Inbound triage is one idea to a company whichever wire carried the
  // message, so it rides the same switch rather than adding a second one that
  // could be left in a state nobody meant.
  email_intent: 'smsIntents',
  // Asked for by a person each time, like the review drafter beside it: the
  // master switch is the only gate it needs, and this names its neighbor in
  // the settings UI.
  reply_draft: 'reviewRequests',
  // Switched by "Transcribe voicemails" in the calls settings, not here; the
  // master switch is the only AI-settings gate, and inbound triage is its
  // natural neighbor.
  voicemail_transcription: 'smsIntents',
  tuning_suggestions: 'tuningSuggestions',
  weekly_digest: 'weeklyDigest',
  follow_up_copy: 'followUpCopy',
  crew_briefing: 'crewBriefings',
  review_request: 'reviewRequests',
  // The reply drafter is a tool the office invokes by hand; it rides the
  // master switch alone, but review_requests is its natural neighbor in the
  // settings UI.
  review_reply: 'reviewRequests',
  quick_quote_parse: 'quickQuotes',
  quick_quote_clarify: 'quickQuotes',
  quick_quote_photo: 'quickQuotes',
  // Reading the photos a customer sends is the same permission whichever form
  // they came through, so photo quotes ride the photo-assessment switch rather
  // than adding a second one that could disagree with it.
  photo_dump_triage: 'photoAssessments',
  photo_dump_synthesis: 'photoAssessments',
} as const;

/** True when this company's settings allow a given action to run. */
export function aiActionEnabled(settings: AiSettings, action: AiAction): boolean {
  if (!settings.enabled) return false;
  // Explicitly requested by a person each time, so the master switch is the
  // only gate either drafter needs. Voicemail transcription has its own
  // switch in the calls settings, checked where the voicemail is processed.
  if (action === 'review_reply' || action === 'reply_draft' || action === 'voicemail_transcription') return true;
  // Which capture methods Magic Quote may use is checked where the capture is
  // taken; by the time an action is being metered, the question is only
  // whether the feature is on at all.
  if (MAGIC_QUOTE_ACTIONS.includes(action)) return settings.magicQuote.enabled;
  return settings[AI_ACTION_TOGGLES[action as keyof typeof AI_ACTION_TOGGLES]];
}

// ---------------------------------------------------------------------------
// The lead draft proposal
// ---------------------------------------------------------------------------

/**
 * What the lead-parsing model may say, and all it may say: rooms chosen from
 * the company's own library by id, quantities, the fixed size/condition
 * codes, and which of the room's own tasks to tick. Everything else on a
 * draft quote (names, minutes, prices) comes from the library and the
 * pricing engine. The Worker validates model output against this before
 * storing it, and the serializer re-parses the stored JSON through it before
 * showing anything — stored data is not trusted twice just for having been
 * validated once.
 */
export const leadProposalRoomSchema = z.object({
  roomTemplateId: z.string(),
  roomName: z.string(),
  quantity: z.number().int().min(1).max(20),
  sizeCode: z.enum(ROOM_SIZE_CODES as [string, ...string[]]),
  conditionCode: z.enum(ROOM_CONDITION_CODES as [string, ...string[]]),
  serviceTemplateIds: z.array(z.string()).max(200),
});

export const leadProposalSchema = z.object({
  summary: z.string().trim().min(1).max(500),
  confidence: z.enum(['low', 'medium', 'high']),
  photoNotes: z.string().trim().max(1000).nullable(),
  rooms: z.array(leadProposalRoomSchema).max(30),
});

export type LeadProposal = z.infer<typeof leadProposalSchema>;

/** The stored column → a validated proposal, or null for anything else. */
export function parseLeadProposal(stored: string | null | undefined): LeadProposal | null {
  if (!stored) return null;
  try {
    const parsed = leadProposalSchema.safeParse(JSON.parse(stored));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
